const PROD_ADMIN_APP_URL = "https://admin.ecotourodisha.com";
const UAT_ADMIN_APP_URL = "https://admin.ecotour.teceads.co.in";
const LOCAL_ADMIN_APP_URL = "http://localhost:5173";
const PROD_SITE_URL = "https://www.ecotourodisha.com";
const UAT_SITE_URL = "https://ecotour.teceads.co.in";

const STATIC_CLIENT_ENV = {
  REACT_APP_ADMIN_WEB_URL: process.env.REACT_APP_ADMIN_WEB_URL || "",
  REACT_APP_ADMIN_APP_URL: process.env.REACT_APP_ADMIN_APP_URL || "",
  REACT_APP_SITE_URL: process.env.REACT_APP_SITE_URL || "",
  PUBLIC_SITE_URL: process.env.PUBLIC_SITE_URL || "",
  SITE_URL: process.env.SITE_URL || "",
  PUBLIC_URL: process.env.PUBLIC_URL || "",
  REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT || "",
  REACT_APP_APP_ENV: process.env.REACT_APP_APP_ENV || "",
};

const PRODUCTION_PORTAL_HOSTS = new Set(["ecotourodisha.com", "www.ecotourodisha.com"]);
const UAT_PORTAL_HOSTS = new Set(["ecotour.teceads.co.in", "www.ecotour.teceads.co.in"]);
const PUBLIC_PORTAL_HOSTS = new Set([...PRODUCTION_PORTAL_HOSTS, ...UAT_PORTAL_HOSTS]);
const ADMIN_PORTAL_HOSTS = new Set([
  "admin.ecotourodisha.com",
  "www.admin.ecotourodisha.com",
  "admin.ecotour.teceads.co.in",
  "www.admin.ecotour.teceads.co.in",
]);

const normalizeBaseUrl = (value = "") => String(value || "").trim().replace(/\/+$/, "");

const sanitizeEnvValue = (value) => {
  const normalized = String(value || "").trim();
  const lower = normalized.toLowerCase();
  if (!normalized) return "";
  if (lower.indexOf("%react_app_") === 0 || lower.indexOf("%vite_") === 0) return "";
  if (normalized.indexOf("%") !== -1) return "";
  return normalized;
};

const readClientEnv = (key) => {
  if (
    typeof window !== "undefined" &&
    window.__PORTAL_BUILD_ENV__ &&
    window.__PORTAL_BUILD_ENV__[key] !== undefined
  ) {
    const runtimeValue = sanitizeEnvValue(window.__PORTAL_BUILD_ENV__[key]);
    if (runtimeValue) return runtimeValue;
  }
  if (
    typeof window !== "undefined" &&
    window.__PORTAL_ENV_DEBUG__ &&
    key === "REACT_APP_ENVIRONMENT"
  ) {
    const runtimeValue = sanitizeEnvValue(window.__PORTAL_ENV_DEBUG__.resolvedEnv);
    if (runtimeValue) return runtimeValue;
  }
  return sanitizeEnvValue(STATIC_CLIENT_ENV[key]);
};

const readHostname = (options = {}) => {
  if (options.hostname !== undefined) {
    return String(options.hostname || "").trim().toLowerCase();
  }
  if (typeof window === "undefined") return "";
  return String(window.location?.hostname || "").trim().toLowerCase();
};

const readOrigin = (options = {}) => {
  if (options.origin !== undefined) return normalizeBaseUrl(options.origin);
  if (typeof window === "undefined") return "";
  return normalizeBaseUrl(window.location?.origin || "");
};

const readEnvironment = () =>
  String(readClientEnv("REACT_APP_ENVIRONMENT") || readClientEnv("REACT_APP_APP_ENV"))
    .trim()
    .toLowerCase();

const isLocalHostname = (hostname) => hostname === "localhost" || hostname === "127.0.0.1";
const isProductionPortalHostname = (hostname) => PRODUCTION_PORTAL_HOSTS.has(hostname);
const isUatPortalHostname = (hostname) =>
  UAT_PORTAL_HOSTS.has(hostname) || hostname.endsWith(".ecotour.teceads.co.in");

const isUatUrl = (value = "") => {
  try {
    const url = new URL(value);
    return url.hostname.toLowerCase().endsWith(".teceads.co.in");
  } catch {
    return String(value || "").toLowerCase().includes(".teceads.co.in");
  }
};

const withPath = (baseUrl, path = "") => {
  const normalizedBase = normalizeBaseUrl(baseUrl);
  const nextPath = String(path || "").trim();
  if (!nextPath) return normalizedBase;
  return `${normalizedBase}${nextPath.startsWith("/") ? nextPath : `/${nextPath}`}`;
};

export const resolveAdminAppBaseUrl = (options = {}) => {
  const explicit = normalizeBaseUrl(
    readClientEnv("REACT_APP_ADMIN_WEB_URL") || readClientEnv("REACT_APP_ADMIN_APP_URL"),
  );
  const hostname = readHostname(options);
  if (isProductionPortalHostname(hostname) && isUatUrl(explicit)) return PROD_ADMIN_APP_URL;
  if (explicit) return explicit;
  if (isLocalHostname(hostname)) {
    return LOCAL_ADMIN_APP_URL;
  }
  if (isProductionPortalHostname(hostname)) {
    return PROD_ADMIN_APP_URL;
  }
  if (isUatPortalHostname(hostname)) {
    return UAT_ADMIN_APP_URL;
  }

  const environment = readEnvironment();
  if (["test", "uat", "staging"].includes(environment)) {
    return UAT_ADMIN_APP_URL;
  }

  return PROD_ADMIN_APP_URL;
};

export const buildAdminAppUrl = (path = "") => {
  return withPath(resolveAdminAppBaseUrl(), path);
};

export const resolveSiteBaseUrl = (options = {}) => {
  const explicit = normalizeBaseUrl(
    readClientEnv("REACT_APP_SITE_URL") ||
      readClientEnv("PUBLIC_SITE_URL") ||
      readClientEnv("SITE_URL") ||
      readClientEnv("PUBLIC_URL"),
  );
  const hostname = readHostname(options);
  const origin = readOrigin(options);
  if (isProductionPortalHostname(hostname) && isUatUrl(explicit)) return origin || PROD_SITE_URL;
  if (explicit) return explicit;
  if (isLocalHostname(hostname)) return origin || "http://localhost:3000";
  if (isProductionPortalHostname(hostname)) return origin || PROD_SITE_URL;
  if (isUatPortalHostname(hostname)) return origin || UAT_SITE_URL;
  return origin || PROD_SITE_URL;
};

export const buildSiteUrl = (path = "") => withPath(resolveSiteBaseUrl(), path);

export const formatUrlForDisplay = (url = "") => String(url || "").replace(/^https?:\/\//i, "");

export const normalizeKnownAppLink = (link = {}) => {
  const path = String(link?.path || "").trim();
  if (!path || !/^https?:\/\//i.test(path)) return link;
  try {
    const url = new URL(path);
    const hostname = url.hostname.toLowerCase();
    const routePath = `${url.pathname || "/"}${url.search || ""}${url.hash || ""}`;
    if (PUBLIC_PORTAL_HOSTS.has(hostname)) {
      return { ...link, path: routePath, external: false };
    }
    if (ADMIN_PORTAL_HOSTS.has(hostname)) {
      return { ...link, path: buildAdminAppUrl(routePath), external: true };
    }
  } catch {
    return link;
  }
  return link;
};
